import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { getSectionWiseDailySummary } from "../../api/attendanceReportApi";
import "./Attendance.css";

const periodApi = createLookupApi("periods");
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Direct port of digicampusbd's `student-attendance-report-section-wise-daily-summary`
 * ("Date Wise Section Summary") — one row per section that took attendance
 * that day/period: present/absent/leave counts, who took it, comments.
 * Confirmed via `sectionWiseSingleDateAttendanceSummary`'s own 6-table
 * native SQL join — new `GET
 * /api/attendance/reports/student/section-wise-daily-summary`
 * (`activeOnly=false`, matching the old app's own `findByInstituteOrderBySerial`
 * scaffold — sections with zero attendance simply don't appear, since this
 * port shows exactly what took attendance rather than padding in every
 * section that exists).
 */
export default function SectionWiseDailySummaryPage() {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    periodApi.list().then((list) => {
      setPeriods(list);
      if (list.length > 0) setPeriodId(String(list[0].id));
    });
  }, []);

  const handleSearch = async () => {
    setError("");
    setLoading(true);
    try {
      setRows(await getSectionWiseDailySummary(date, periodId, false));
    } catch (err) {
      setError("Failed to load section summary.");
    } finally {
      setLoading(false);
    }
  };

  const totalTaken = (rows || []).reduce((s, r) => s + r.attendanceTaken, 0);
  const totalPresent = (rows || []).reduce((s, r) => s + r.present, 0);
  const totalAbsent = (rows || []).reduce((s, r) => s + r.absent, 0);
  const totalLeave = (rows || []).reduce((s, r) => s + r.leave, 0);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Date Wise Section Summary</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      <div className="attendance-filters">
        <label>
          Period
          <select value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        rows && (
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Class</th>
                  <th>Shift</th>
                  <th>Section</th>
                  <th>Taken</th>
                  <th>Present</th>
                  <th>Absent</th>
                  <th>Leave</th>
                  <th>Taken By</th>
                  <th>Taken At</th>
                  <th>Comments</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.classConfigId}>
                    <td>{r.className}</td>
                    <td>{r.shiftName}</td>
                    <td>{r.sectionName}</td>
                    <td>{r.attendanceTaken}</td>
                    <td>{r.present}</td>
                    <td>{r.absent}</td>
                    <td>{r.leave}</td>
                    <td>{r.takenBy}</td>
                    <td>{r.takenTime}</td>
                    <td>{r.comments}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={10}>No section took attendance for this selection.</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} style={{ fontWeight: 800, textAlign: "right" }}>
                    Grand Total
                  </td>
                  <td>
                    <strong>{totalTaken}</strong>
                  </td>
                  <td>
                    <strong>{totalPresent}</strong>
                  </td>
                  <td>
                    <strong>{totalAbsent}</strong>
                  </td>
                  <td>
                    <strong>{totalLeave}</strong>
                  </td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}
    </MainLayout>
  );
}
